// Preferencing simulator — pure random valid-ranking generator (hidden admin tool).
//
// Produces a randomized-but-VALID ranked preference set for a single applicant, matching the Scholars
// preferencing rules the student form enforces (BR-PR1/PR2/PR5):
//   - ranks are a contiguous run starting at 1 (no gaps)
//   - ranks 1–3 are required, so at least REQUIRED_COUNT programs must be ranked
//   - ranks 4–6 are optional (MAX_RANK = 6)
//   - a program may appear at most once
//
// Output shape matches the backend ScholarsPreferenceInput[]: [{ subProgramId, rank }].
// Pure + deterministic when a custom `rng` is injected (used by the unit test); defaults to Math.random.

export const REQUIRED_COUNT = 3;
export const MAX_RANK = 6;

/**
 * Fisher–Yates shuffle (non-mutating). `rng` returns a float in [0, 1).
 * @template T
 * @param {T[]} arr
 * @param {() => number} rng
 * @returns {T[]}
 */
function shuffle(arr, rng) {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/**
 * Generate one random valid ranking for an applicant.
 *
 * @param {string[]} openProgramIds - subProgramIds of OPEN programs (the ranking pool).
 * @param {object} [options]
 * @param {() => number} [options.rng] - injectable RNG (float in [0,1)); defaults to Math.random.
 * @param {number} [options.maxRank] - cap on how many ranks to fill (defaults to MAX_RANK).
 * @returns {{ subProgramId: string, rank: number }[]} contiguous ranks starting at 1.
 * @throws {Error} when fewer than REQUIRED_COUNT distinct programs are available (can't build a valid set).
 */
export function generateRandomRankings(openProgramIds = [], options = {}) {
    const { rng = Math.random, maxRank = MAX_RANK } = options;

    // De-dupe + drop falsy ids so the pool only contains distinct, selectable programs.
    const pool = [...new Set((openProgramIds ?? []).filter(Boolean))];

    if (pool.length < REQUIRED_COUNT) {
        throw new Error(
            `Cannot generate preferences: need at least ${REQUIRED_COUNT} open programs, got ${pool.length}.`
        );
    }

    // Upper bound on ranks: never exceed MAX_RANK, the requested cap, or the pool size.
    const hardMax = Math.min(MAX_RANK, maxRank, pool.length);

    // Pick a random count in [REQUIRED_COUNT, hardMax] so some applicants rank only 3 and others more.
    const span = hardMax - REQUIRED_COUNT;
    const count = REQUIRED_COUNT + Math.floor(rng() * (span + 1));

    const picks = shuffle(pool, rng).slice(0, count);
    return picks.map((subProgramId, i) => ({ subProgramId, rank: i + 1 }));
}

/**
 * Convenience: random alternate opt-in (defaults to Math.random). Kept here so the dialog's randomness is
 * all pure + testable in one place.
 * @param {() => number} [rng]
 * @returns {boolean}
 */
export function randomAlternateOptIn(rng = Math.random) {
    return rng() < 0.5;
}
